import { BitkitAlertProps } from '@bitrise/bitkit-v2';
import { useContext, useEffect } from 'react';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { SocialLoginProviderKeys, SocialLoginProviders } from '@/constants';
import useAlert from '@/hooks/useAlert';
import { getQueryParam } from '@/utils/queryParams';

const useSignInFlashMessage = () => {
  const { setErrorAlert, setWarningAlert, setInfoAlert } = useAlert();
  const flashKey = getQueryParam('flash');
  const { flash } = useContext(GlobalPropsContext);
  const errorMessageDebugInfo = flash?.errorMessageDebugInfo as string;

  useEffect(() => {
    let message = '';
    let level: BitkitAlertProps['variant'] = 'critical';

    switch (flashKey) {
      case 'confirmed':
        message = 'You have successfully confirmed your email address';
        level = 'info';
        break;
      case 'unconfirmed':
        message =
          'Please confirm your email address to be able to log in with SSO. You can do so by clicking on the link in the confirmation email we sent when you created your account.';
        break;
      case 'invalid-confirmation':
        message = 'This is an invalid URL or this account is already confirmed. Please try logging in.';
        break;
      case 'unlocked':
        message = 'Your account has been unlocked. Please log in to continue.';
        level = 'info';
        break;
      case 'invalid-unlock':
        message =
          'This unlock link is invalid or has already been used. Please try logging in, or reset your password if your account is still locked.';
        break;
      case 'sso-email-taken': {
        const { email, provider } = (flash?.ssoFlashProps as { email: string; provider: string }) || {};
        if (email && provider) {
          setWarningAlert(
            `Email ${email} already found - log in with your email and link your ${
              SocialLoginProviders[provider as SocialLoginProviderKeys] || provider
            } account from your profile page.`,
          );
        }
        break;
      }
      case 'sso-authorize-email':
        message =
          "You need to authorize your organization to use SSO to log you in. Check your email - we've sent you a link to approve the authorization.";
        break;
      case 'enable-sso-login':
        message =
          'You have to be logged in the first time you authorize your organization to log you in with SSO. Please log in with your original credentials first and then visit the SSO login URL.';
        break;
      case 'no-saml-sso-identity':
        message = 'There is no SAML SSO identity for this user.';
        break;
      case 'other-saml-sso-identity':
        message = 'You have enabled SAML SSO with another organization.';
        break;
      case 'sso-identity-already-in-use':
        message = `${errorMessageDebugInfo}`;

        break;
      case 'sso-error':
        message =
          'Something went wrong while trying to log in via SAML SSO. Please try to log in again or contact your SAML SSO admin for further information.';

        if (errorMessageDebugInfo) {
          message += ` ${errorMessageDebugInfo}`;
        }

        break;
      default:
        break;
    }

    if (message !== '') {
      switch (level) {
        case 'info':
          setInfoAlert(message);

          break;
        case 'critical':
          setErrorAlert(message);

          break;
        default:
          break;
      }
    }
  }, [flash, flashKey, errorMessageDebugInfo, setErrorAlert, setWarningAlert, setInfoAlert]);
};

export default useSignInFlashMessage;
